import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { StorageService } from '@services/core/storage.service';
import { AuthService } from '@services/core/auth.service';

export type CrudFlag = 'canRead' | 'canCreate' | 'canUpdate' | 'canDelete';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  public appHasAccess = ['ADMIN', 'SUPPORT', 'MANAGER'];
  private storageService = inject(StorageService);
  private authService = inject(AuthService);
  private router = inject(Router);

  canAccess(): boolean {
    const userRole = String(this.storageService.getJsonValue('role')?.name ?? 'VISUALIZADOR').toUpperCase();
    return this.appHasAccess.includes(userRole);
  }

  /** Flag CRUD del menú indicado para el rol activo. */
  can(path: string, flag: CrudFlag): boolean {
    return this._flagOf(this._findNode(this._menus(), this._norm(path)), flag);
  }

  /** Flag CRUD del menú que cubre la ruta actual (mismo criterio que RouteGuard). */
  canCurrent(flag: CrudFlag): boolean {
    return this.can(this._currentPath(), flag);
  }

  /** Verifica si el rol activo tiene la accion o comodin (*) en el menu indicado. */
  hasAction(path: string, actionKey: string, fallback?: CrudFlag): boolean {
    const node = this._findNode(this._menus(), this._norm(path));
    if (!node) return false;
    const actions = this._safeActions(node.actions);
    if (actions.includes('*')) return true;
    if (actions.length > 0) return actions.includes(actionKey);
    return fallback ? this._flagOf(node, fallback) : false;
  }

  private _safeActions(raw: unknown): string[] {
    if (Array.isArray(raw)) return raw;
    if (typeof raw !== 'string' || !raw.trim()) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** Verifica si el rol activo tiene la accion o comodin (*) en la ruta actual. */
  hasCurrentAction(actionKey: string, fallback?: CrudFlag): boolean {
    return this.hasAction(this._currentPath(), actionKey, fallback);
  }

  private _menus(): any[] {
    const live = this.authService.sharingMenu() as any[];
    return live?.length ? live : (this.storageService.getJsonValue('menu') || []);
  }

  private _currentPath(): string {
    return this._norm(this.router.url.split('?')[0]);
  }

  private _norm(path: string): string {
    return String(path || '').replace(/^\//, '').toLowerCase();
  }

  private _findNode(menus: any[], path: string): any | null {
    for (const item of menus || []) {
      const hit = this._matchNode(item, path);
      if (hit) return hit;
    }
    return null;
  }

  private _matchNode(item: any, path: string): any | null {
    const child = this._findNode(item.submenu || [], path);
    if (child) return child;
    return this._selfMatches(item, path) ? item : null;
  }

  private _selfMatches(item: any, path: string): boolean {
    const url = this._norm(item.url || item.path);
    return !!url && (url === path || path.startsWith(url + '/'));
  }

  private _flagOf(node: any, flag: CrudFlag): boolean {
    if (!node) return false;
    if (flag === 'canRead') return node.canRead !== false;
    return !!node[flag];
  }
}
